import type { GetStaticPathsResult } from 'next';
import { ConfigProvider } from 'shared/config/ConfigContext';
import hotelData from 'shared/seo/data/hotel.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage as OldDestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, hotelSearchFactory } from '@/components/factory';
import { DestinationPage as NewDestinationPage } from '@/components/NewDestinationPage';
import { config, feConfig } from '@/config';
import { getCountriesForDestination, hotelCities, hotelCountries, hotelMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageProps, SeoPageContext } from '@/interfaces/pages';
import { pageContentUrl, pageFor } from '@/lib/hotelContent';
import { fetchPagesMetadata } from '@/lib/pages-metadata';

const allPagesMetadataUrl = config.get('hotel.allCountryPlaceDestinationPagesMetadata');

export const getStaticPaths = async (): Promise<GetStaticPathsResult> => {
    const allPagesMetadata: Record<string, string>[] = await fetchPagesMetadata(allPagesMetadataUrl).catch((err) => {
        console.error(err);
        return err;
    });

    const destinations = allPagesMetadata.map((page: { place: string; country: string }) => ({
        params: {
            cityLevel1: page.place,
            countryLevel1: page.country,
        },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const { countryLevel1, cityLevel1 } = context.params;
    const page = await pageFor(pageContentUrl.forCountryPlaceDestination(countryLevel1, cityLevel1));

    const destination = hotelCities.find(
        (place) => place.countryPath === countryLevel1.toLowerCase() && place.cityPath === cityLevel1.toLowerCase(),
    );
    const destinationCountry = hotelCountries.find((place) => place.countryPath === countryLevel1.toLowerCase());

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return {
            notFound: true,
        };
    }

    const popularCities = (hotelMapping[destinationCountry.id] ?? []).filter((mapped) => mapped.city !== destination.city).slice(0, 11);
    const popularCountries = getCountriesForDestination(destinationCountry, hotelCountries);

    return {
        props: {
            destination: {
                ...destination,
                continent: destinationCountry.continent,
            },
            content: page.htmlContent ?? null,
            popularCities,
            popularCountries,
            config: feConfig,
        },
        revalidate: 3600,
    };
};

const Inner = ({ destination, content, popularCities, popularCountries }: Omit<CityPageProps, 'config'>) => {
    const factory = cityLevel1Factory(destination, hotelData, hotelSearchFactory());

    const DestinationPage = content ? NewDestinationPage : OldDestinationPage;

    return (
        <DestinationPage factory={factory} vertical={verticals.hotel} data={hotelData}>
            {content ? <div className="mb-64" dangerouslySetInnerHTML={{ __html: content }} /> : null}
            <PopularCities
                destination={destination}
                cities={popularCities}
                cdnUrl={hotelData.cdnPath}
                verticalUrl={hotelData.basePath}
                lang={hotelData.lang}
            />
            <PopularCountries
                destination={destination}
                countries={popularCountries}
                cdnUrl={hotelData.cdnPath}
                verticalUrl={hotelData.basePath}
                lang={hotelData.lang}
            />
            <LinkTree
                heading={hotelData.lang.linkTree.heading}
                description={hotelData.lang.linkTree.description}
                data={hotelData.linkTree}
                url={hotelData.basePath}
            />
        </DestinationPage>
    );
};

const CityLevel1 = ({ destination, content, popularCities, popularCountries, config }: CityPageProps) => (
    <ConfigProvider config={config}>
        <Inner destination={destination} content={content} popularCities={popularCities} popularCountries={popularCountries} />
    </ConfigProvider>
);

export default CityLevel1;
